// src/admin/pages/AdminPaymentReconciliation.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
// 🟢 Imported List and Clock icons for the dark tabs
import { ShieldAlert, CheckCircle, RotateCcw, X, Info, List, Clock } from 'lucide-react';
import Swal from 'sweetalert2'; // 👈 SweetAlert2 imported

// 🟢 Centralized adminApi endpoints imported
import { 
    getReconciliationList, 
    getRefundedReconciliationList, 
    processSingleReconciliation 
} from '../../services/adminApi';

import './AdminPaymentReconciliation.css'; 

const AdminPaymentReconciliation = () => {
    const [activeTab, setActiveTab] = useState('eligible'); 
    const [pendingLogs, setPendingLogs] = useState([]);
    const [refundedLogs, setRefundedLogs] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Modal Control States
    const [showModal, setShowModal] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [cancelReason, setCancelReason] = useState('Candidate Requested');
    const [remark, setRemark] = useState('');
    const [isManualRefund, setIsManualRefund] = useState(false);

    const fetchPendingLogs = async () => {
        setLoading(true);
        try {
            const response = await getReconciliationList();
            setPendingLogs(response.data.data || []);
        } catch (err) {
            console.error('Could not load reconciliation logs.', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchRefundedLogs = async () => {
        setLoading(true);
        try {
            const response = await getRefundedReconciliationList();
            setRefundedLogs(response.data.data || []);
        } catch (err) {
            console.error('Could not load refunded logs.', err);
        } finally {
            setLoading(false);
        }
    };

    // 🟢 Fetch data automatically when active tab changes
    useEffect(() => {
        if (activeTab === 'eligible') {
            fetchPendingLogs();
        } else {
            fetchRefundedLogs();
        }
    }, [activeTab]);

    const handleOpenReconcileModal = (log) => {
        setSelectedLog(log);
        setCancelReason('Candidate Requested');
        setRemark('');
        setIsManualRefund(false);
        setShowModal(true);
    };

    const handleConfirmReconcile = async (e) => {
        e.preventDefault();
        if (!selectedLog) return;

        setActionLoading(true);
        console.log("👉 [Admin Reconciliation] Initiating Reconcile for Booking ID:", selectedLog.id);
        console.log("👉 [Admin Reconciliation] Payload:", { reason: cancelReason, remark, forceCancel: isManualRefund });

        try {
            const res = await processSingleReconciliation(selectedLog.id, {
                reason: cancelReason,
                remark: remark,
                forceCancel: isManualRefund
            });

            console.log("✅ [Admin Reconciliation] Success Response:", res.data);

            // 🟢 SweetAlert2 for success notification
            Swal.fire({
                title: 'Success!',
                text: res.data.message || 'Refund has been successfully initiated and booking cancelled.',
                icon: 'success',
                background: '#151521',
                color: '#ffffff',
                confirmButtonColor: '#10B981',
                customClass: {
                    popup: 'admin-payments-card text-white'
                }
            });

            setShowModal(false);
            if (activeTab === 'eligible') {
                fetchPendingLogs();
            } else {
                fetchRefundedLogs();
            }
        } catch (err) {
            console.error("❌ [Admin Reconciliation] Request Failed!");
            console.error("❌ [Admin Reconciliation] Status:", err.response?.status);
            console.error("❌ [Admin Reconciliation] Response Data:", err.response?.data);
            console.error("❌ [Admin Reconciliation] Full Error:", err);

            const serverMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to process refund.';

            if (err.response?.data?.canForceCancel) {
                setActionLoading(false);
                const result = await Swal.fire({
                    title: 'Gateway Refund Notice',
                    html: `<div style="text-align: left; font-size: 0.95rem;">
                        <p class="text-warning mb-2"><b>Reason:</b> ${serverMsg}</p>
                        <p class="text-light mb-0">The payment could not be processed directly through the Razorpay gateway (e.g. test payment ID, already refunded on dashboard, or uncaptured transaction). Would you like to mark this booking as <b>CANCELLED & REFUNDED</b> in the system?</p>
                    </div>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, Force Cancel & Mark Refunded',
                    cancelButtonText: 'Cancel',
                    confirmButtonColor: '#EF4444',
                    cancelButtonColor: '#4B5563',
                    background: '#151521',
                    color: '#ffffff',
                    customClass: {
                        popup: 'admin-payments-card text-white'
                    }
                });

                if (result.isConfirmed) {
                    try {
                        setActionLoading(true);
                        const forceRes = await processSingleReconciliation(selectedLog.id, {
                            reason: cancelReason,
                            remark: remark,
                            forceCancel: true
                        });

                        Swal.fire({
                            title: 'Success!',
                            text: forceRes.data.message || 'Booking status updated to CANCELLED.',
                            icon: 'success',
                            background: '#151521',
                            color: '#ffffff',
                            confirmButtonColor: '#10B981',
                            customClass: {
                                popup: 'admin-payments-card text-white'
                            }
                        });

                        setShowModal(false);
                        if (activeTab === 'eligible') {
                            fetchPendingLogs();
                        } else {
                            fetchRefundedLogs();
                        }
                    } catch (forceErr) {
                        Swal.fire({
                            title: 'Error!',
                            text: forceErr.response?.data?.message || forceErr.message,
                            icon: 'error',
                            background: '#151521',
                            color: '#ffffff',
                            confirmButtonColor: '#DC2626'
                        });
                    } finally {
                        setActionLoading(false);
                    }
                }
                return;
            }

            // 🟢 SweetAlert2 for general error notification showing exact message
            Swal.fire({
                title: 'Refund Error!',
                text: serverMsg,
                icon: 'error',
                background: '#151521',
                color: '#ffffff',
                confirmButtonColor: '#DC2626',
                customClass: {
                    popup: 'admin-payments-card text-white'
                }
            });
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="container-fluid py-4 admin-payments-container">
            <div className="mb-4">
                <h2 className="admin-payments-title">Reconciliation Engine</h2>
                <p className="admin-payments-subtitle">Cancel audition booking slots and initiate automated Razorpay gateway refunds.</p>
            </div>

            {/* 🟢 Dark-themed tab buttons with no inline styles */}
            <div className="d-flex gap-2 mb-4 p-1 rounded-3 admin-payments-card" style={{ width: 'fit-content' }}>
                <Button 
                    className={`btn-sm admin-reconciliation-tab-btn ${activeTab === 'eligible' ? 'active' : ''}`}
                    onClick={() => setActiveTab('eligible')}
                >
                    <List size={16} /> Active Bookings ({pendingLogs.length})
                </Button>
                <Button 
                    className={`btn-sm admin-reconciliation-tab-btn ${activeTab === 'refunded' ? 'active' : ''}`}
                    onClick={() => setActiveTab('refunded')}
                >
                    <Clock size={16} /> Refunded History ({refundedLogs.length})
                </Button>
            </div>

            {/* 🟢 TAB 1: ACTIVE BOOKINGS (ELIGIBLE FOR REFUND) */}
            {activeTab === 'eligible' && (
                <Card className="border-0 rounded-4 overflow-hidden mb-4 admin-payments-card">
                    <div className="table-responsive">
                        <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless admin-payments-table">
                            <thead className="admin-payments-thead">
                                <tr className="admin-payments-thead-tr">
                                    <th className="px-4 py-3 text-uppercase">#</th>
                                    <th className="py-3 text-uppercase">Application ID</th>
                                    <th className="py-3 text-uppercase">User Name</th>
                                    <th className="py-3 text-uppercase">Payment ID</th>
                                    <th className="py-3 text-uppercase">Amount</th>
                                    <th className="py-3 text-uppercase">Current Status</th>
                                    <th className="py-3 text-uppercase">Initiated Date</th>
                                    <th className="px-4 py-3 text-center text-uppercase">Action</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: '0.9rem' }}>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="text-center p-5 admin-payments-text-secondary">
                                            <Spinner animation="border" size="sm" className="me-2" /> Loading active bookings...
                                        </td>
                                    </tr>
                                ) : pendingLogs.length > 0 ? (
                                    pendingLogs.map((log, idx) => (
                                        <tr key={log.id} className="admin-payments-tbody-tr">
                                            <td className="px-4 admin-payments-text-secondary">{idx + 1}</td>
                                            <td className="fw-semibold text-white font-monospace">{log.application_id}</td>
                                            <td className="fw-semibold text-white">{log.user_name}</td>
                                            <td className="font-monospace admin-payments-text-secondary small">{log.payment_id}</td>
                                            <td className="fw-bold text-white">₹{Number(log.amount).toFixed(2)}</td>
                                            <td>
                                                <Badge bg="success" className="text-uppercase">
                                                    {log.payment_status}
                                                </Badge>
                                            </td>
                                            <td className="admin-payments-text-secondary">{new Date(log.created_at).toLocaleString('en-IN')}</td>
                                            <td className="px-4 text-center">
                                                <Button variant="danger" size="sm" className="admin-payments-btn-danger d-inline-flex align-items-center gap-1 text-nowrap" onClick={() => handleOpenReconcileModal(log)}>
                                                    <RotateCcw size={14} /> Refund & Cancel
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center p-5 admin-payments-text-secondary">
                                            <ShieldAlert size={36} className="mb-2 text-success" />
                                            <p className="m-0 text-white fw-semibold">No active bookings eligible for cancellation found.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )}

            {/* 🟢 TAB 2: REFUNDED HISTORY (AUDITED REFUNDS HISTORY) */}
            {activeTab === 'refunded' && (
                <Card className="border-0 rounded-4 overflow-hidden mb-4 admin-payments-card">
                    <div className="table-responsive">
                        <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless admin-payments-table">
                            <thead className="admin-payments-thead">
                                <tr className="admin-payments-thead-tr">
                                    <th className="px-4 py-3 text-uppercase">#</th>
                                    <th className="py-3 text-uppercase">Payment ID</th>
                                    <th className="py-3 text-uppercase">Refund ID</th>
                                    <th className="py-3 text-uppercase">Order ID</th>
                                    <th className="py-3 text-uppercase">User Name</th>
                                    <th className="py-3 text-uppercase">Email</th>
                                    <th className="py-3 text-uppercase">Mobile Number</th>
                                    <th className="py-3 text-uppercase">Reason</th>
                                    <th className="py-3 text-uppercase">Refund Amount</th>
                                    <th className="py-3 text-uppercase">Initiated Date</th>
                                </tr>
                            </thead>
                            <tbody style={{ fontSize: '0.9rem' }}>
                                {loading ? (
                                    <tr>
                                        <td colSpan="10" className="text-center p-5 admin-payments-text-secondary">
                                            <Spinner animation="border" size="sm" className="me-2" /> Loading refunded logs...
                                        </td>
                                    </tr>
                                ) : refundedLogs.length > 0 ? (
                                    refundedLogs.map((log, idx) => (
                                        <tr key={log.id} className="admin-payments-tbody-tr">
                                            <td className="px-4 admin-payments-text-secondary">{idx + 1}</td>
                                            <td className="font-monospace text-white small">{log.payment_id}</td>
                                            <td className="font-monospace text-danger small fw-semibold">{log.refund_id}</td>
                                            <td className="font-monospace admin-payments-text-secondary small">{log.order_id}</td>
                                            <td className="fw-semibold text-white">{log.user_name}</td>
                                            <td className="text-secondary">{log.email}</td>
                                            <td className="text-secondary font-monospace">{log.mobile_no}</td>
                                            <td className="text-secondary small">{log.reason || "-"}{log.remark ? ` (${log.remark})` : ""}</td>
                                            <td className="fw-bold text-danger">₹{Number(log.amount).toFixed(2)}</td>
                                            <td className="admin-payments-text-secondary">{new Date(log.refund_date).toLocaleString('en-IN')}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="text-center p-5 admin-payments-text-secondary">
                                            <ShieldAlert size={36} className="mb-2 text-danger" />
                                            <p className="m-0 text-white fw-semibold">No refund history found in database.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card>
            )}

            {/* Reconciliation Audit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered contentClassName="admin-payments-card text-white">
                <Modal.Header className="admin-payments-border-bottom p-4 border-secondary">
                    <Modal.Title className="fw-bold text-white d-flex align-items-center gap-2 admin-payments-modal-title">
                        <RotateCcw size={22} className="text-danger" /> Confirm Cancellation & Refund
                    </Modal.Title>
                    {/* 🟢 Sleek borderless custom close button */}
                    <button className="admin-payments-modal-close" onClick={() => setShowModal(false)}>
                        <X size={18} />
                    </button>
                </Modal.Header>
                <Modal.Body className="p-4 admin-payments-modal-body">
                    {selectedLog && (
                        <Form onSubmit={handleConfirmReconcile}>
                            {isManualRefund ? (
                                <Alert className="d-flex align-items-start gap-2 border-0 py-3 rounded-3 text-warning" style={{ backgroundColor: 'rgba(234, 179, 8, 0.12)', border: '1px solid rgba(234, 179, 8, 0.3)' }}>
                                    <Info size={18} className="mt-1 flex-shrink-0" />
                                    <div>
                                        <strong className="d-block mb-1 text-warning">Manual / Offline Mode Active</strong>
                                        Razorpay Gateway API call will be bypassed. The booking will be marked as CANCELLED & REFUNDED in the database and saved to the refund history.
                                    </div>
                                </Alert>
                            ) : (
                                <Alert className="d-flex align-items-start gap-2 border-0 py-3 rounded-3 admin-payments-alert-refund-warning">
                                    <Info size={18} className="mt-1 flex-shrink-0" />
                                    <div>
                                        <strong className="text-white d-block mb-1">Warning: Real Money Gateway Refund</strong>
                                        This action will trigger the active Razorpay API to issue a real money refund of ₹{Number(selectedLog.amount).toFixed(2)} to the candidate's original payment method.
                                    </div>
                                </Alert>
                            )}

                            <Form.Group className="mb-3 p-3 rounded-3" style={{ backgroundColor: '#1A1C28', border: '1px dashed #3B3F58' }}>
                                <Form.Check 
                                    type="checkbox"
                                    id="manual-refund-checkbox"
                                    label="Manual / Offline Refund (Skip Razorpay API)"
                                    className="text-white fw-semibold small"
                                    checked={isManualRefund}
                                    onChange={(e) => setIsManualRefund(e.target.checked)}
                                />
                                <div className="text-secondary small mt-1 ps-4">
                                    Check this option if this is a test booking, does not exist on the payment gateway, or the refund was already processed offline/via dashboard.
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-white fw-semibold small">Cancellation Reason <span className="text-danger">*</span></Form.Label>
                                <Form.Select 
                                    className="border-0 rounded-3 text-white py-2 admin-payments-select w-100" 
                                    value={cancelReason} 
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    style={{ backgroundColor: '#1E1F29', border: '1px solid #2D3142' }}
                                >
                                    <option value="Candidate Requested">Candidate Requested</option>
                                    <option value="Audition Rescheduled">Audition Rescheduled</option>
                                    <option value="CD Cancelled Event">CD Cancelled Event</option>
                                    <option value="Duplicate Payment">Duplicate Payment</option>
                                    <option value="Other">Other Reasons</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-white fw-semibold small">Internal Remarks</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Add any internal auditing notes..."
                                    className="rounded-3 text-white admin-payments-input admin-payments-input-group px-3 py-2"
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    maxLength={500}
                                />
                            </Form.Group>

                            <div className="d-flex gap-2 mt-4">
                                <Button variant="outline-secondary" className="w-50 py-2 border rounded-3 bg-transparent text-secondary admin-payments-btn-secondary text-nowrap" onClick={() => setShowModal(false)}>
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit" 
                                    className={`w-50 py-2 rounded-3 text-white border-0 text-nowrap ${isManualRefund ? 'fw-bold' : 'admin-payments-btn-refund'}`} 
                                    disabled={actionLoading}
                                    style={isManualRefund ? { backgroundColor: '#F59E0B', color: '#000000' } : {}}
                                >
                                    {actionLoading ? <Spinner size="sm" /> : isManualRefund ? 'Confirm Manual Refund' : 'Confirm & Refund'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AdminPaymentReconciliation;